export const ISP_OPTIONS = [
  { value: "Xfinity", label: "Xfinity (Comcast)" },
  { value: "AT&T", label: "AT&T" },
  { value: "Spectrum", label: "Spectrum (Charter)" },
  { value: "Cox", label: "Cox" },
  { value: "Optimum", label: "Optimum (Altice)" },
  { value: "Frontier", label: "Frontier" },
  { value: "Windstream", label: "Windstream" },
  { value: "CenturyLink", label: "CenturyLink / Lumen" },
  { value: "Wow!", label: "Wow!" },
  { value: "Other", label: "Other" },
] as const;

export type IspProvider = typeof ISP_OPTIONS[number]["value"];

// Outage-check page URLs for each supported ISP.
//
// Every URL below was verified live in a headless browser (June 2026): each
// returns HTTP 200 and lands on the provider's actual outage tool, not a 404
// or a redirect to the homepage. Where the page exposes an address/ZIP entry,
// it is noted — the host lands there and types their address into the
// provider's own form.
//
// Preloading: none of these providers populate their address/ZIP field from a
// URL query parameter. This was tested empirically (Frontier and AT&T were
// each probed with several param names — service-address/address/zip/zipCode/…
// — and every one was ignored). So we link to the deepest correct outage page
// and the host enters their address on the provider's form. The `zipCode`
// argument to getOutageUrl is therefore accepted but currently unused; keep it
// so callers don't have to change if a provider later adds param support.
const ISP_OUTAGE_URLS: Record<string, string> = {
  // Xfinity outage map — has an "Enter service address" field. (The previously
  // used outage.xfinity.com host does NOT resolve.)
  "Xfinity": "https://www.xfinity.com/support/statusmap",
  // AT&T outage page — has a ZIP code field (account sign-in for detail).
  "AT&T": "https://www.att.com/outages/",
  // Spectrum storm center — Spectrum has no fully public, no-login outage tool;
  // this is the canonical outage page (sign-in for address-specific results).
  "Spectrum": "https://www.spectrum.net/page/spectrum-storm-center",
  // Cox outage troubleshooting — has ZIP + street-address entry.
  "Cox": "https://www.cox.com/residential/support/outages.html",
  // Optimum outage map — has city/ZIP entry.
  "Optimum": "https://www.optimum.com/outage-map",
  // Frontier service-outage check — has a service-address field.
  "Frontier": "https://frontier.com/pages/helpcenter/internet/troubleshooting/check-for-service-outages",
  // Windstream is now "Kinetic by Windstream" — outages & service interruptions page.
  "Windstream": "https://www.gokinetic.com/support/internet-security/internet-outages-and-service-interruptions",
  // CenturyLink "Check for Outages" — has a ZIP code field.
  "CenturyLink": "https://www.centurylink.com/home/help/internet/internet-or-phone-not-working.html",
  // WOW! (WideOpenWest) maintenance & outages page.
  "Wow!": "https://www.wowway.com/help-center/maintenance-outages",
  // "Other": fall back to Downdetector's generic homepage.
  "Other": "https://downdetector.com",
};

const FALLBACK_URL = "https://downdetector.com";

/**
 * Converts an arbitrary ISP name to a Downdetector path slug.
 * Downdetector slugs are lowercase alphanumeric strings with hyphens.
 * e.g. "My ISP Name!" → "my-isp-name"
 */
function toDowndetectorSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Returns the outage status URL for the given ISP provider.
 * - Known ISPs: returns their verified outage-check page.
 * - Unknown ISP with a name: returns a Downdetector pre-filtered URL.
 * - No ISP set: returns the generic Downdetector homepage.
 *
 * @param ispProvider - ISP name as stored on the property (e.g. "Xfinity")
 * @param zipCode - Property ZIP code. Accepted for forward-compatibility but
 *   currently unused: no supported provider prefills its form from a URL param
 *   (verified June 2026). Left in the signature so callers don't change if a
 *   provider later adds param support.
 */
export function getOutageUrl(ispProvider: string | null | undefined, zipCode?: string | null): string {
  void zipCode;
  if (!ispProvider) return FALLBACK_URL;
  const entry = ISP_OUTAGE_URLS[ispProvider];
  if (entry === undefined) {
    // Unknown ISP — link to Downdetector pre-filtered by provider name where possible.
    const slug = toDowndetectorSlug(ispProvider);
    return slug ? `https://downdetector.com/status/${slug}/` : FALLBACK_URL;
  }
  return entry;
}

/**
 * Extracts a 5-digit US ZIP code from a free-form address string.
 * Returns null when no ZIP is found (e.g. address is incomplete or missing).
 */
export function extractZipFromAddress(address: string | null | undefined): string | null {
  if (!address) return null;
  const match = address.match(/\b(\d{5})(?:-\d{4})?\b/);
  return match ? match[1] : null;
}
